import { Component, Prop } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';
import VueBase from '@/VueBase';
import { locales } from '@/i18n-setup';
import helper, { _loadComponent } from '@/services/helper';
import Q from 'q';
import _, { toLower } from 'lodash';
import api from '@/services/api';
import { Route, RawLocation } from 'vue-router';

@Component<VoteMixins>({
  components: {
    SideBar: (resolve) => _loadComponent('SideBar/', resolve),
  },
  computed: {
    ...mapState({
      CandidatesPerRow: 'CandidatesPerRow',
      ShowCandidateName: 'ShowCandidateName',
      ShowCandidatePicture: 'ShowCandidatePicture',
      ShowCandidateDetails: 'ShowCandidateDetails',
      ShowConfirm: 'ShowConfirm',
      ShowSelectSubject: 'ShowSelectSubject',
      ShowSideBar: 'ShowSideBar',
      isSingleOptionSelect: 'IsOptions',
      CanCancelVote: 'CanCancelVote',
    }),
    ...mapGetters({
      PrevSubject: 'PrevSubject',
      NextSubject: 'NextSubject',
    }),
  },
  watch: {
    subjectIndex(val) {
      if (_.isNumber(Number(val))) this.loadSubject();
    },
  },
})
export default class VoteMixins extends VueBase {
  public readonly CandidatesPerRow!: number;
  public readonly ShowCandidateName!: boolean;
  public readonly ShowCandidatePicture!: boolean;
  public readonly ShowCandidateDetails!: boolean;
  public readonly ShowSelectSubject!: boolean;
  public readonly ShowSideBar!: string;
  public readonly ShowConfirm!: boolean;
  public readonly isSingleOptionSelect!: string;
  public readonly PrevSubject!: string;
  public readonly NextSubject!: string;

  @Prop() public readonly subjectIndex!: number;

  get IsFirst() {
    return this.PrevSubject === null;
  }

  get IsLast() {
    return this.NextSubject === null;
  }

  get NextText() {
    return this.IsLast || this.ShowConfirm ? this.$t('vs.vote.navigation.next.label') : this.NextSubject;
  }

  get Groups() {
    return (
      this.subject?.Groups.map((x) => ({
        Id: x.Id,
        Name: x.Name,
        Candidates: x.Candidates,
        Selected: _.difference(x.Candidates, this.selectedCandidateIds).length == 0,
      })) || []
    );
  }

  public onlySelected: boolean = false;
  public subject: SubjectModel | null = null;
  public candidates: CandidateSimpleModel[] = [];

  get candidateWidth() {
    if (document.body.clientWidth > 991) {
      return 100.0 / this.CandidatesPerRow;
    }
    if (process.env.VUE_APP_CLIENT == 'cpa') {
      return 50.0;
    }
    return 100.0;
  }

  get pluralCount() {
    return (this.subject?.Max || 0) > 1 ? 'multi' : 'single';
  }

  get selectedCandidates() {
    return this.candidates.filter((x) => x.Selected);
  }

  get selectedCandidateIds() {
    return this.selectedCandidates.map((x) => x.Id);
  }

  get showInstructionsLine1() {
    return process.env.VUE_APP_CLIENT != 'cpa';
  }

  mounted() {
    this.subject = null;
    this.loadSubject();
  }

  public async loadSubject() {
    helper.swalShowLoading();

    await Q.delay(1000);
    this.$store.commit('SelectSubject', this.subjectIndex);
    await Q.delay(100);
    this.subject = this.$store.getters.Subject;
    const selected: string[] = this.$store.getters.SubjectData || [];
    if (!this.subject) {
      this.$router.push({ name: 'Start' });
      return false;
    }
    this.candidates = this.subject.Candidates.map((x) => ({
      Id: x.Id,
      Enum: x.Enum,
      DisplayName: x.DisplayName,
      PictureURL: x.PictureURL,
      PictureURL150x150: x.PictureURL150x150,
      Selected: selected.includes(x.Id),
    }));

    helper.close();
    this.scrollToTop();
    this.onLoaded();
    return true;
  }

  // ToOverride if needed
  onLoaded() {}

  scrollToTop() {
    [
      window,
      ...document.body.getElementsByClassName('page-content'),
      ...document.body.getElementsByClassName('scroll-content'),
    ].forEach((el) => {
      el.scrollTo(0, 0);
    });
  }

  public onSelectGroup(group: SubjectGroupModel) {
    if (!this.subject) return;

    let groupCandidates = this.candidates.filter((x) => group.Candidates.includes(x.Id));

    if (group.Selected) {
      groupCandidates.forEach((x) => (x.Selected = false));
      return;
    } else {
      groupCandidates = groupCandidates.filter((x) => !x.Selected);

      var max = this.subject.Max;
      var selected = this.selectedCandidates.length + groupCandidates.length;
      if (selected > max) {
        helper.swalWarning({
          title: this.$t('vs.vote.popup.maxselected.title') as string,
          text: this.$t('vs.vote.popup.maxselected.text') as string,
        });
        return;
      }

      groupCandidates.forEach((x) => (x.Selected = true));
    }
  }

  public onCandidateClick(candidate: CandidateSimpleModel) {
    if (!this.subject) return;

    var max = this.subject.Max;
    var selected = this.selectedCandidates.length;
    if (!candidate.Selected && selected === max) {
      helper.swalWarning({
        title: this.$t('vs.vote.popup.maxselected.title') as string,
        text: this.$t('vs.vote.popup.maxselected.text') as string,
      });
      return;
    }
    candidate.Selected = !candidate.Selected;
  }

  public onRemove(id: string) {
    let candidate = this.candidates?.find((x) => x.Id == id);
    if (!candidate) return;
    candidate.Selected = false;
  }

  public async onCancelVote() {
    var confirms = await helper.cancelVote();
    if (!confirms) return;

    if (process.env.VUE_APP_MODE === 'SA') {
      this.$router.push({ name: 'VoterAssign' });
    }
    if (process.env.VUE_APP_MODE === 'WEB') {
      this.$router.push({ name: 'Login' });
    }
    if (process.env.VUE_APP_MODE === 'CS') {
      this.$router.push({ name: 'Start' });
    }
  }

  public onCandidateDetailsClick(candidate: CandidateModel, event: Event) {
    event.stopPropagation();
    var candidateFull = this.subject?.Candidates.find((x) => x.Id == candidate.Id);
    this.$modal.show('candidate-details', candidateFull);
  }

  public async onGoNext() {
    if (!this.subject) return;

    var min = this.subject.Min;
    var max = this.subject.Max;
    var whitePaper = this.subject.WhitePaper;
    var selected = this.selectedCandidates.length;
    if (selected == 0 && whitePaper) {
      this.skipClick();
      return;
    }
    if (selected > max) {
      return;
    } else if (selected < min) {
      helper.swalWarning({
        text: this.$t('vs.vote.popup.min.title', [min, max]) as string,
        confirmButtonText: this.$t('vs.vote.popup.min.confirm') as string,
      });
      return;
    } else if (selected >= min && selected < max) {
      var n = max - selected;

      var confirm = await helper.swalConfirm({
        showCancelButton: true,
        title: this.$t('vs.vote.popup.minmax.title', [n]) as string,
        text: this.$t('vs.vote.popup.minmax.text', [n]) as string,
        confirmButtonText: this.$t('vs.vote.popup.minmax.confirm') as string,
        cancelButtonText: this.$t('vs.vote.popup.minmax.cancel') as string,
      });
      if (!confirm) return;
    }
    this.saveAndConfirm();
  }
  public async skipClick() {
    if (!this.subject) return;

    if (!this.subject.WhitePaper) {
      return false;
    }

    var confirm = await helper.swalConfirm({
      title: this.$t('vs.vote.popup.skip.title') as string,
      text: this.$t('vs.vote.popup.skip.text') as string,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: this.$t('vs.vote.popup.skip.confirm') as string,
      cancelButtonText: this.$t('vs.vote.popup.skip.cancel') as string,
      confirmButtonColor: 'green',
      cancelButtonColor: 'gray',
    });
    if (!confirm) return;

    this.candidates.forEach((x) => (x.Selected = false));

    this.saveAndConfirm();
  }

  private async saveAndConfirm() {
    this.$store.commit('SaveSubjectVoting', {
      Id: this.subjectIndex,
      Selected: this.selectedCandidateIds,
    });
    let reload: boolean = false;

    var route: RawLocation = {};

    if (this.IsLast && this.IsFirst) {
      route.name = 'FinalConfirm';
    } else {
      let nextId = this.subjectIndex;
      if (this.ShowConfirm) {
        route.name = 'SubjectConfirm';
      } else {
        nextId++;
        route.name = this.IsLast ? 'FinalConfirm' : 'Vote';
        helper.swalShowLoading();
        reload = !this.IsLast;
      }
      await Q.delay(250);
      route.params = { subjectIndex: String(nextId) };
    }

    this.$router.push(route);
    if (reload) await this.loadSubject();
  }

  public onGoPrev() {
    if (this.IsFirst) {
      if (!this.ShowSelectSubject) return;
      this.$router.replace({
        name: 'Start',
      });
    }
    let goToSubject = Number(this.subjectIndex) - 1;

    if (this.ShowConfirm) {
      this.$router.push({
        name: 'SubjectConfirm',
        params: { subjectIndex: String(goToSubject) },
      });
    } else {
      this.$router.push({
        name: 'Vote',
        params: { subjectIndex: String(goToSubject) },
      });
      this.loadSubject();
    }
  }
}
